import type { ReactNode } from "react"
import { ChevronRight } from "lucide-react"

import { cn } from "@/lib/utils"

type PageHeaderProps = {
	readonly title: ReactNode
	readonly eyebrow?: ReactNode
	readonly breadcrumbs?: ReactNode
	readonly actions?: ReactNode
	readonly back?: ReactNode
	readonly className?: string
}

type PageBreadcrumbsProps = {
	readonly items: readonly ReactNode[]
	readonly className?: string
}

export function PageBreadcrumbs({ items, className }: PageBreadcrumbsProps) {
	return (
		<nav
			aria-label="Breadcrumb"
			className={cn(
				"pt-1 [&_a]:transition-colors [&_a:hover]:text-foreground [&_a:focus-visible]:text-foreground",
				className,
			)}
		>
			<ol className="font-inter text-meta font-normal flex flex-wrap items-center gap-1.5 text-muted-foreground">
				{items.map((item, index) => (
					<li key={index} className="flex items-center gap-1.5">
						{index > 0 ? <ChevronRight className="size-4" strokeWidth={1.5} /> : null}
						<span className="font-medium">{item}</span>
					</li>
				))}
			</ol>
		</nav>
	)
}

export function PageHeader({ title, eyebrow, breadcrumbs, actions, back, className }: PageHeaderProps) {
	return (
		<div className={cn("space-y-4", className)}>
			{breadcrumbs ? breadcrumbs : null}
			<div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
				<div className="space-y-2">
					{eyebrow ? (
						<p className="font-inter text-label font-semibold leading-[1.2] tracking-[0.16em] uppercase text-brand">{eyebrow}</p>
					) : null}
					<div className="flex items-center gap-3">
						{back}
						<h1 className="font-avenir text-page-title font-semibold text-foreground">{title}</h1>
					</div>
				</div>
				{actions ? <div className="flex items-center gap-2">{actions}</div> : null}
			</div>
		</div>
	)
}
